import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FiRefreshCw } from 'react-icons/fi';
import services from '../../services';

const parseDateStr = (str) => (str ? new Date(`${str}T00:00:00`) : null);
const formatDateStr = (date) => {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const FIELD_OPTIONS = [
    { value: '', label: 'Select field' },
    { value: 'task.title', label: 'Title' },
    { value: 'task.subject', label: 'Subject' },
    { value: 'task.name', label: 'Name' },
    { value: 'user.name', label: 'Assignee' },
    { value: 'customProperty', label: 'Property' },
    { value: 'task.id', label: 'Ticket Ref' },
    { value: 'trip.tripId', label: 'Trip Ref' },
    { value: 'email', label: 'Email' },
    { value: 'bookingRef', label: 'Booking Ref' },
    { value: 'customerName', label: 'Customer Name' },
];

const SORT_BY_OPTIONS = [
    { value: 'updatedAt', label: 'Updated' },
    { value: 'createdAt', label: 'Created' },
    // { value: 'title', label: 'Title' },
    // { value: 'deadline', label: 'Deadline' },
    // { value: 'priority', label: 'Priority' },
    // { value: 'user', label: 'Assignee' },
    // { value: 'boardList', label: 'Column' },
];

const SORT_ORDER_OPTIONS = [
    { value: 'DESC', label: 'Newest first' },
    { value: 'ASC', label: 'Oldest first' },
];

const DATE_FILTER_OPTIONS = [
    { value: 'All_Dates', label: 'All Dates' },
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'thisWeek', label: 'This Week' },
    { value: 'lastWeek', label: 'Last Week' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'lastMonth', label: 'Last Month' },
    { value: 'thisYear', label: 'This Year' },
    { value: 'customRange', label: 'Custom Range' },
];

const GREEN = '#1a7d3e';
const EMPTY_ROW = { field: '', value: '' };

export const DEFAULT_FILTERS = {
    search: '',
    boardId: [],
    sortBy: 'updatedAt',
    sortOrder: 'DESC',
    createdAtFilter: 'All_Dates',
    startDate: '',
    endDate: '',
    advanced: [{ ...EMPTY_ROW }],
};

const labelClass = 'text-[11px] font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap';

const selectWrapClass = 'flex items-center gap-1.5';

const Dropdown = ({ options, value, onChange, minWidth = '150px' }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handleOutsideClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [open]);

    const selected = options.find((o) => o.value === value);

    return (
        <div ref={ref} style={{ position: 'relative', minWidth }}>
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                style={{
                    width: '100%',
                    height: '32px',
                    padding: '0 26px 0 10px',
                    fontSize: '13px',
                    color: '#1f2937',
                    border: `1px solid ${open ? GREEN : '#d1d5db'}`,
                    borderRadius: '6px',
                    background: '#fff',
                    outline: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <span>{selected?.label}</span>
                <svg style={{ position: 'absolute', right: '8px' }} width="12" height="12" viewBox="0 0 16 16" fill="#6b7280">
                    <path d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" />
                </svg>
            </button>
            {open && (
                <ul style={{ position: 'absolute', top: '36px', left: 0, zIndex: 9999, width: '100%', minWidth, background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px', boxShadow: '0 6px 16px rgba(0,0,0,0.1)', padding: '4px 0', margin: 0, listStyle: 'none' }}>
                    {options.map((opt) => (
                        <li
                            key={opt.value}
                            onClick={() => { onChange(opt.value); setOpen(false); }}
                            style={{ padding: '8px 12px', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap', color: opt.value === value ? GREEN : '#1f2937', background: opt.value === value ? '#f0fdf4' : 'transparent', fontWeight: opt.value === value ? 600 : 400 }}
                            onMouseEnter={(e) => { if (opt.value !== value) e.currentTarget.style.background = '#f9fafb'; }}
                            onMouseLeave={(e) => { if (opt.value !== value) e.currentTarget.style.background = 'transparent'; }}
                        >
                            {opt.label}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const dateInputClass = 'border rounded-md text-sm px-2.5 py-1.5 bg-white text-gray-800 focus:outline-none';

const ArchivedFilters = ({ filters, setFilters, onSearchClear }) => {
    const [draft, setDraft] = useState(filters);
    const [users, setUsers] = useState([]);
    const [properties, setProperties] = useState([]);
    const [fieldOpenIndex, setFieldOpenIndex] = useState(null);
    const [assigneeOpenIndex, setAssigneeOpenIndex] = useState(null);
    const [propertyOpenIndex, setPropertyOpenIndex] = useState(null);
    const [searchTermAssignee, setSearchTermAssignee] = useState('');
    const [searchTermProperty, setSearchTermProperty] = useState('');
    const [statusOpenIndex, setStatusOpenIndex] = useState(null);
    const [customFieldsByProperty, setCustomFieldsByProperty] = useState({});

    const fieldDropdownRef = useRef(null);
    const assigneeDropdownRef = useRef(null);
    const propertyDropdownRef = useRef(null);
    const statusDropdownRef = useRef(null);

    useEffect(() => {
        const query = new URLSearchParams();
        query.append('falsePagination', true);
        services.user.GET_ALL_USER(query)
            .then((data) => setUsers(data?.users || (Array.isArray(data) ? data : data?.data || [])))
            .catch(() => setUsers([]));
    }, []);

    useEffect(() => {
        const firstBoardId = filters.boardId?.[0];
        if (!firstBoardId) {
            setProperties([]);
            return;
        }
        services.property.GET_PROPERTY_BY_BOARD_ID(firstBoardId)
            .then((res) => setProperties(res?.data?.data || (Array.isArray(res?.data) ? res.data : [])))
            .catch(() => setProperties([]));
    }, [filters.boardId]);

    useEffect(() => {
        if (fieldOpenIndex === null) return;
        const handleOutsideClick = (e) => {
            if (fieldDropdownRef.current && !fieldDropdownRef.current.contains(e.target)) setFieldOpenIndex(null);
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [fieldOpenIndex]);

    useEffect(() => {
        if (assigneeOpenIndex === null) return;
        const handleOutsideClick = (e) => {
            if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(e.target)) {
                setAssigneeOpenIndex(null);
                setSearchTermAssignee('');
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [assigneeOpenIndex]);

    useEffect(() => {
        if (propertyOpenIndex === null) return;
        const handleOutsideClick = (e) => {
            if (propertyDropdownRef.current && !propertyDropdownRef.current.contains(e.target)) {
                setPropertyOpenIndex(null);
                setSearchTermProperty('');
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [propertyOpenIndex]);

    useEffect(() => {
        if (statusOpenIndex === null) return;
        const handleOutsideClick = (e) => {
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) setStatusOpenIndex(null);
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [statusOpenIndex]);

    useEffect(() => {
        const propertyIds = Array.from(new Set(
            (draft.advanced || [])
                .filter((f) => f.field === 'customProperty' && Array.isArray(f.value))
                .flatMap((f) => f.value.map((p) => p.value))
                .filter(Boolean)
        ));
        const missingIds = propertyIds.filter((id) => !(id in customFieldsByProperty));
        if (missingIds.length === 0) return;

        let cancelled = false;
        (async () => {
            const entries = await Promise.all(missingIds.map(async (propertyId) => {
                try {
                    const res = await services.customfield.GET_CUSTOM_FIELD_BY_TASK_ID(propertyId);
                    return [propertyId, res?.data?.data || []];
                } catch (error) {
                    console.error('Error fetching status options for property:', error);
                    return [propertyId, []];
                }
            }));
            if (!cancelled) setCustomFieldsByProperty((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
        })();
        return () => { cancelled = true; };
    }, [draft.advanced, customFieldsByProperty]);

    const userOptions = users.map((u) => ({ label: u.name || u.email || `User ${u.id}`, value: u.id }));
    const propertyOptions = (Array.isArray(properties) ? properties : []).map((p) => ({
        label: p?.property?.propertyName || p?.name || 'Unnamed Property',
        value: p?.property?.id || p?.propertyId || p?.id,
    }));
    const updateDraft = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

    const updateRow = (index, updates) => {
        setDraft((prev) => ({
            ...prev,
            advanced: prev.advanced.map((row, i) => (i === index ? { ...row, ...updates } : row)),
        }));
    };

    const removeRow = (index) => {
        const remaining = draft.advanced.filter((_, i) => i !== index);
        updateDraft('advanced', remaining.length ? remaining : [{ ...EMPTY_ROW }]);
    };

    const addRow = () => updateDraft('advanced', [...draft.advanced, { ...EMPTY_ROW }]);

    const toggleAssigneeUser = (index, user) => {
        const currentVal = Array.isArray(draft.advanced[index]?.value) ? draft.advanced[index].value : [];
        const exists = currentVal.find((u) => u.value === user.value);
        const newVal = exists ? currentVal.filter((u) => u.value !== user.value) : [...currentVal, user];
        updateRow(index, { value: newVal });
    };

    const toggleProperty = (index, property) => {
        const currentVal = Array.isArray(draft.advanced[index]?.value) ? draft.advanced[index].value : [];
        const exists = currentVal.find((p) => p.value === property.value);
        const newVal = exists ? currentVal.filter((p) => p.value !== property.value) : [...currentVal, property];
        updateRow(index, { value: newVal, optionValue: [] });
    };

    const toggleStatusOption = (index, option) => {
        const currentVal = Array.isArray(draft.advanced[index]?.optionValue) ? draft.advanced[index].optionValue : [];
        const exists = currentVal.find((o) => o.value === option.value);
        updateRow(index, { optionValue: exists ? currentVal.filter((o) => o.value !== option.value) : [...currentVal, option] });
    };

    const applyFilters = () => {
        setFilters((prev) => ({ ...prev, ...draft, search: prev.search, boardId: prev.boardId }));
    };

    const resetFilters = () => {
        setDraft((prev) => ({ ...DEFAULT_FILTERS, boardId: prev.boardId }));
        setFilters((prev) => ({ ...DEFAULT_FILTERS, search: '', boardId: prev.boardId }));
        if (onSearchClear) onSearchClear();
    };

    const renderFieldRow = (index) => {
        const row = draft.advanced[index];
        return (
            <div key={index} className={selectWrapClass}>
                <span className={labelClass} style={{ minWidth: '38px' }}>Field:</span>
                <div ref={fieldOpenIndex === index ? fieldDropdownRef : null} style={{ position: 'relative', minWidth: '130px' }}>
                    <button
                        type="button"
                        onClick={() => setFieldOpenIndex(fieldOpenIndex === index ? null : index)}
                        style={{
                            width: '100%',
                            height: '32px',
                            padding: '0 26px 0 10px',
                            fontSize: '13px',
                            color: row.field ? '#1f2937' : '#9ca3af',
                            border: `1px solid ${fieldOpenIndex === index ? GREEN : '#d1d5db'}`,
                            borderRadius: '6px',
                            background: '#fff',
                            outline: 'none',
                            cursor: 'pointer',
                            textAlign: 'left',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <span>{FIELD_OPTIONS.find((o) => o.value === row.field)?.label || 'Select field'}</span>
                        <svg style={{ position: 'absolute', right: '8px' }} width="12" height="12" viewBox="0 0 16 16" fill="#6b7280">
                            <path d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" />
                        </svg>
                    </button>
                    {fieldOpenIndex === index && (
                        <ul style={{ position: 'absolute', top: '36px', left: 0, zIndex: 9999, width: '160px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px', boxShadow: '0 6px 16px rgba(0,0,0,0.1)', padding: '4px 0', margin: 0, listStyle: 'none' }}>
                            {FIELD_OPTIONS.map((opt) => (
                                <li
                                    key={opt.value}
                                    onClick={() => {
                                        const field = opt.value;
                                        const value = (field === 'user.name' || field === 'customProperty') ? [] : '';
                                        updateRow(index, { field, value, optionValue: [] });
                                        setFieldOpenIndex(null);
                                    }}
                                    style={{ padding: '8px 12px', fontSize: '13px', cursor: 'pointer', color: row.field === opt.value ? GREEN : '#1f2937', background: row.field === opt.value ? '#f0fdf4' : 'transparent', fontWeight: row.field === opt.value ? 600 : 400 }}
                                >
                                    {opt.label}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>is</span>

                {row.field === 'user.name' ? (
                    <div className="relative" ref={assigneeOpenIndex === index ? assigneeDropdownRef : null}>
                        <button
                            type="button"
                            onClick={() => setAssigneeOpenIndex(assigneeOpenIndex === index ? null : index)}
                            style={{ height: '32px', padding: '0 10px', fontSize: '13px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', minWidth: '160px' }}
                        >
                            <span style={{ color: Array.isArray(row.value) && row.value.length > 0 ? GREEN : '#9ca3af' }}>
                                {Array.isArray(row.value) && row.value.length > 0 ? `${row.value.length} selected` : 'Select assignees...'}
                            </span>
                        </button>
                        {assigneeOpenIndex === index && (
                            <div className="absolute left-0 mt-1 bg-white z-[9999] overflow-hidden" style={{ minWidth: '220px', maxHeight: '260px', borderRadius: '10px', border: '1px solid #d1d5db', boxShadow: '0 8px 24px rgba(26,92,50,0.10)' }}>
                                <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f4f1', background: '#fafcfb' }}>
                                    <input
                                        type="text"
                                        placeholder="Search users..."
                                        style={{ width: '100%', padding: '5px 8px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '6px', outline: 'none' }}
                                        value={searchTermAssignee}
                                        onChange={(e) => setSearchTermAssignee(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                                <div className="overflow-y-auto" style={{ maxHeight: '170px' }}>
                                    {userOptions
                                        .filter((u) => u.label.toLowerCase().includes(searchTermAssignee.toLowerCase()))
                                        .map((user) => {
                                            const isChecked = (Array.isArray(row.value) ? row.value : []).some((u) => u.value === user.value);
                                            return (
                                                <label key={user.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 12px', cursor: 'pointer', background: isChecked ? '#f0f9f2' : 'transparent' }}>
                                                    <input type="checkbox" checked={isChecked} onChange={() => toggleAssigneeUser(index, user)} style={{ accentColor: GREEN }} />
                                                    <span style={{ fontSize: '13px', color: '#374151' }}>{user.label}</span>
                                                </label>
                                            );
                                        })}
                                </div>
                            </div>
                        )}
                    </div>
                ) : row.field === 'customProperty' ? (
                    <div className="relative" ref={propertyOpenIndex === index ? propertyDropdownRef : null}>
                        <button
                            type="button"
                            onClick={() => setPropertyOpenIndex(propertyOpenIndex === index ? null : index)}
                            style={{ height: '32px', padding: '0 10px', fontSize: '13px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', minWidth: '160px' }}
                        >
                            <span style={{ color: Array.isArray(row.value) && row.value.length > 0 ? GREEN : '#9ca3af' }}>
                                {Array.isArray(row.value) && row.value.length > 0 ? `${row.value.length} selected` : 'Select properties...'}
                            </span>
                        </button>
                        {propertyOpenIndex === index && (
                            <div className="absolute left-0 mt-1 bg-white z-[9999] overflow-hidden" style={{ minWidth: '220px', maxHeight: '260px', borderRadius: '10px', border: '1px solid #d1d5db', boxShadow: '0 8px 24px rgba(26,92,50,0.10)' }}>
                                <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f4f1', background: '#fafcfb' }}>
                                    <input
                                        type="text"
                                        placeholder="Search properties..."
                                        style={{ width: '100%', padding: '5px 8px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '6px', outline: 'none' }}
                                        value={searchTermProperty}
                                        onChange={(e) => setSearchTermProperty(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                                <div className="overflow-y-auto" style={{ maxHeight: '170px' }}>
                                    {propertyOptions.length === 0 ? (
                                        <div style={{ padding: '12px', fontSize: '12px', color: '#9ca3af', textAlign: 'center' }}>Select a board to load properties</div>
                                    ) : propertyOptions
                                        .filter((p) => p.label.toLowerCase().includes(searchTermProperty.toLowerCase()))
                                        .map((property) => {
                                            const isChecked = (Array.isArray(row.value) ? row.value : []).some((p) => p.value === property.value);
                                            return (
                                                <label key={property.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 12px', cursor: 'pointer', background: isChecked ? '#f0f9f2' : 'transparent' }}>
                                                    <input type="checkbox" checked={isChecked} onChange={() => toggleProperty(index, property)} style={{ accentColor: GREEN }} />
                                                    <span style={{ fontSize: '13px', color: '#374151' }}>{property.label}</span>
                                                </label>
                                            );
                                        })}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <input
                        type="text"
                        style={{ height: '32px', padding: '0 10px', fontSize: '13px', border: '1px solid #d1d5db', borderRadius: '6px', outline: 'none', width: '160px', color: '#1f2937' }}
                        value={typeof row.value === 'string' ? row.value : ''}
                        onChange={(e) => updateRow(index, { value: e.target.value })}
                        placeholder="Enter value"
                    />
                )}

                {row.field === 'customProperty' && Array.isArray(row.value) && row.value.length > 0 && (() => {
                    const availableOptions = row.value
                        .flatMap((p) => customFieldsByProperty[p.value] || [])
                        .flatMap((cf) => {
                            const raw = cf?.customField?.value;
                            const items = Array.isArray(raw) ? raw : [raw];
                            return items.map((item) => ({
                                label: item && typeof item === 'object' ? String(item.label ?? item.name ?? item.value ?? '') : String(item ?? ''),
                                value: cf.id,
                                color: (item && typeof item === 'object' && item.color) || cf?.customField?.color,
                            }));
                        })
                        .filter((o) => o.label)
                        .filter((o, i, arr) => arr.findIndex((x) => x.value === o.value && x.label === o.label) === i);
                    const isOpen = statusOpenIndex === index;
                    const selectedVals = Array.isArray(row.optionValue) ? row.optionValue : [];
                    return (
                        <div className="relative" ref={isOpen ? statusDropdownRef : null}>
                            <button
                                type="button"
                                onClick={() => setStatusOpenIndex(isOpen ? null : index)}
                                style={{ height: '32px', padding: '0 10px', fontSize: '13px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', minWidth: '160px' }}
                            >
                                <span style={{ color: selectedVals.length > 0 ? GREEN : '#9ca3af' }}>
                                    {selectedVals.length > 0 ? `${selectedVals.length} selected` : 'Any status'}
                                </span>
                            </button>
                            {isOpen && (
                                <div className="absolute left-0 mt-1 bg-white z-[9999] overflow-hidden" style={{ minWidth: '200px', maxHeight: '260px', borderRadius: '10px', border: '1px solid #d1d5db', boxShadow: '0 8px 24px rgba(26,92,50,0.10)' }}>
                                    {selectedVals.length > 0 && (
                                        <div style={{ padding: '5px 12px', borderBottom: '1px solid #e9f3ec', background: '#f0f9f2', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <span style={{ fontSize: '11px', color: GREEN, fontWeight: 500 }}>{selectedVals.length} selected</span>
                                            <button type="button" onClick={() => updateRow(index, { optionValue: [] })} style={{ fontSize: '11px', color: GREEN, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}>Clear all</button>
                                        </div>
                                    )}
                                    <div className="overflow-y-auto" style={{ maxHeight: '220px' }}>
                                        {availableOptions.length === 0 ? (
                                            <div style={{ padding: '12px', fontSize: '12px', color: '#9ca3af', textAlign: 'center' }}>No statuses found</div>
                                        ) : availableOptions.map((option) => {
                                            const isChecked = selectedVals.some((o) => o.value === option.value);
                                            return (
                                                <label key={`${option.value}-${option.label}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 12px', cursor: 'pointer', background: isChecked ? '#f0f9f2' : 'transparent' }}>
                                                    <input type="checkbox" checked={isChecked} onChange={() => toggleStatusOption(index, option)} style={{ accentColor: GREEN }} />
                                                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0, background: option.color || '#9ca3af' }} />
                                                    <span style={{ fontSize: '13px', color: '#374151' }}>{option.label}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })()}

                <button
                    onClick={() => removeRow(index)}
                    title="Remove filter"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '26px', height: '26px', borderRadius: '6px', border: '1px solid #fcd5d5', background: '#fff5f5', color: '#ef4444', cursor: 'pointer', flexShrink: 0 }}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854z" />
                    </svg>
                </button>
            </div>
        );
    };

    return (
        <div className="archived-filters bg-white border-b px-4 py-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className={selectWrapClass}>
                    <span className={labelClass}>Sort By:</span>
                    <Dropdown options={SORT_BY_OPTIONS} value={draft.sortBy} onChange={(v) => updateDraft('sortBy', v)} />
                </div>

                <div className={selectWrapClass}>
                    <span className={labelClass}>Order:</span>
                    <Dropdown options={SORT_ORDER_OPTIONS} value={draft.sortOrder} onChange={(v) => updateDraft('sortOrder', v)} minWidth="140px" />
                </div>

                <div className={selectWrapClass}>
                    <span className={labelClass}>Date:</span>
                    <Dropdown options={DATE_FILTER_OPTIONS} value={draft.createdAtFilter} onChange={(v) => updateDraft('createdAtFilter', v)} minWidth="160px" />
                </div>

                {draft.createdAtFilter === 'customRange' && (
                    <>
                        <div className={selectWrapClass}>
                            <span className={labelClass}>Start:</span>
                            <DatePicker
                                selected={parseDateStr(draft.startDate)}
                                onChange={(date) => updateDraft('startDate', formatDateStr(date))}
                                dateFormat="MM/dd/yyyy"
                                placeholderText="mm/dd/yyyy"
                                maxDate={parseDateStr(draft.endDate) || undefined}
                                className={dateInputClass}
                                calendarClassName="archived-filters-datepicker"
                            />
                        </div>
                        <div className={selectWrapClass}>
                            <span className={labelClass}>End:</span>
                            <DatePicker
                                selected={parseDateStr(draft.endDate)}
                                onChange={(date) => updateDraft('endDate', formatDateStr(date))}
                                dateFormat="MM/dd/yyyy"
                                placeholderText="mm/dd/yyyy"
                                minDate={parseDateStr(draft.startDate) || undefined}
                                className={dateInputClass}
                                calendarClassName="archived-filters-datepicker"
                            />
                        </div>
                    </>
                )}

                <button
                    type="button"
                    onClick={resetFilters}
                    title="Reset filters"
                    className="p-1.5 rounded-md border text-gray-500 hover:text-gray-700"
                    style={{ borderColor: '#d1d5db', flexShrink: 0 }}
                >
                    <FiRefreshCw size={14} />
                </button>

                <div className="flex items-center gap-2 ml-auto" style={{ flexShrink: 0 }}>
                    <button
                        type="button"
                        onClick={applyFilters}
                        className="px-5 py-1.5 rounded-full text-sm font-medium text-white"
                        style={{ backgroundColor: GREEN }}
                    >
                        Filter
                    </button>
                    <button
                        type="button"
                        onClick={resetFilters}
                        className="px-5 py-1.5 rounded-full text-sm font-medium text-white"
                        style={{ backgroundColor: GREEN }}
                    >
                        Clear
                    </button>
                </div>
            </div>

            <div style={{ height: '1px', background: '#fff', margin: '6px 0' }} />

            <div className="flex flex-col gap-2 items-start">
                {draft.advanced.map((_, index) => renderFieldRow(index))}
            </div>

            <button
                type="button"
                onClick={addRow}
                title="Add filter"
                style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12.5px', fontWeight: 500, color: GREEN, background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: '8px' }}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
                </svg>
                Add filter
            </button>
        </div>
    );
};

export default ArchivedFilters;
